<script>
import DialogBase from "../../dialog/components/dialog-base.vue";
import DialogHeader from "../../dialog/components/dialog-header.vue";
import DialogBody from "../../dialog/components/dialog-body.vue";
import DialogActionbar from "../../dialog/components/dialog-actionbar.vue";
import {AddData} from "./AddData";
import TextField from "../../common/text-field.vue";
import API from "../../../API";
import SelectField from "../../common/select-field.vue";
import FilterAutocomplete from "../common/table/filter-autocomplete.vue";
import Checkbox from '../common/PrimeVue/Checkbox.vue'
import toaster from "../../common/toaster";
import ContentDialogLoading from "./content-dialog-loading.vue";
import ContentDialogField from "./content-dialog-field.vue";

export default {
    name: "add-content-dialog",
    components: {
        ContentDialogField,
        ContentDialogLoading,
        FilterAutocomplete,
        SelectField,
        TextField,
        DialogActionbar,
        DialogBody,
        DialogHeader,
        DialogBase,
        Checkbox
    },
    props: {
        title: String,
        addUrl: String,
        getUrl: String,
        redirectUrl: String,
    },
    data() {
        return {
            showDialog: false,
            loading: false,
            data: new AddData(),
            layers: [],
            tags: [],
            dynamic: false,
        }
    },
    computed: {
        translations() {
            return {
                confirmText: Lang.langGet('Add'),
                custom: Lang.langGet('Custom Resolution'),
                added: Lang.langGet('Added successfully!')
            }
        },
        dialogTitle() {
            return this.dynamic ? Lang.langGet('Add new dynamic campaign') : this.title;
        },
        field() {
            const {data, layerItems, tagItems} = this;

            return {
                name: {type: 'text', value: data.name, property: 'name'},
                label: {type: 'select', value: data.label, property: 'label', items: layerItems},
                useLayer: {type: 'checkbox', value: data.useLayer, property: 'useLayer'},
                width: {type: 'number', value: data.width, property: 'width'},
                height: {type: 'number', value: data.height, property: 'height'},
                format: {type: 'text', value: data.format, property: 'format'},
                tags: {type: 'multiSelect', value: data.tags, property: 'tags', items: tagItems},
                strict: {type: 'checkbox', value: data.strictModeTags, property: 'strictModeTags', hint: Lang.langGet('In \"Strict Mode\" elements must have ALL selected tags.')},
                autoRemove: {type: 'checkbox', value: data.dynamicAutoremove, property: 'dynamicAutoremove', hint: Lang.langGet('If an element that is used in this campaign loses a tag which is defined above, it will be also removed from this campaign.')}
            }
        },
        tagItems() {
            return this.tags.map(tag => ({...tag, value: tag.name}));
        },
        layerItems() {
            const layers = [{value: this.data.customResolution(), text: this.translations.custom}]
            this.layers.forEach(layer => layers.push(layer));
            return layers;
        },
        customResolutionSelected() {
            return this.data.label === this.data.customResolution();
        },
        disableSave() {
            return !this.data.validate();
        }
    },
    methods: {
        closeDialog() {
            this.showDialog = false;
        },
        async handleAdd() {
            await this.save();
            this.showDialog = false;
        },
        async save() {
            this.loading = true;
            const api = new API(this.addUrl);
            const data = this.data.prepareDataForSave(this.tags);

            if (this.dynamic) data.dynamic = this.dynamic;

            const result = await api.post(data);
            this.loading = false;

            if (!result.errored) {
                this.errored = false;
                toaster.success(this.translations.added);
                setTimeout(() => {
                    window.location = `${this.redirectUrl}${result.id}/latest`;
                }, 200);
            }
        },
        handleChange(property, value) {
            this.data.update(property, value);
        },
        async fetchDefaults() {
            this.loading = true;
            const api = new API(this.getUrl);
            const {layers, tags, height, width} = await api.get();
            this.layers = layers;
            this.tags = tags;

            const ratio = aspectRatio(width, height);
            this.data = new AddData({width, height, ratio});
            this.loading = false;
        }
    },
    async mounted() {
        EaseEventBus.listen('showAddContentDialog', async ({dynamic}) => {
            this.showDialog = true;
            this.dynamic = dynamic;
            await this.fetchDefaults();
        });
    }
}
</script>

<template>
    <dialog-base
        :maxWidth="600"
        @close="closeDialog"
        :showDialog="showDialog"
    >
        <dialog-header
            @close="closeDialog"
            :title="dialogTitle"
        />
        <dialog-body :min-height="dynamic ? 392 : 336">
            <content-dialog-loading v-if="loading" />
            <div v-else class="pa-4 fade-in">
                <content-dialog-field
                    :params="field.name"
                    autofocus
                    required
                    @input="handleChange"
                />
                <content-dialog-field
                    :params="field.label"
                    @input="handleChange"
                />
                <div class="mb-6 mt-3">
                    <content-dialog-field
                        :params="field.useLayer"
                        :disabled="customResolutionSelected"
                        @input="handleChange"
                    />
                </div>
                <div class="d-flex flex-row">
                    <content-dialog-field
                        :params="field.width"
                        :disabled="!customResolutionSelected"
                        @input="handleChange"
                    />
                    <content-dialog-field
                        :params="field.height"
                        :disabled="!customResolutionSelected"
                        @input="handleChange"
                    />
                    <content-dialog-field
                        :params="field.format"
                        disabled
                    />
                </div>
                <content-dialog-field
                    :params="field.tags"
                    @input="handleChange"
                />
                <div class="mt-3">
                    <content-dialog-field v-if="dynamic"
                        :params="field.strict"
                        @input="handleChange"
                    />
                </div>
                <content-dialog-field v-if="dynamic"
                    :params="field.autoRemove"
                    @input="handleChange"
                />
            </div>
        </dialog-body>
        <dialog-actionbar
            :disable-save="disableSave"
            noIcons
            :confirmText="translations.confirmText"
            @close="closeDialog"
            @save="handleAdd"
        />
    </dialog-base>
</template>
